/**
 * DealPeopleCard.tsx — People on this deal (deal detail slice 8; spec §3.5 A
 * and §3.6 "Link contact"; R5 of §39.9). Replaces slice 3's one-contact card.
 *
 * LINKED: one row per linked contact (GET /v1/sales/deals/{id}/contacts, 0133)
 * — avatar, name, "{title} · Primary contact" on the primary, email and phone
 * (the rider: nothing the old card showed is lost), "Log call", and for a
 * non-primary row "Set primary"; every row has "Unlink", behind a confirmation
 * that names the contact and the deal. NONE LINKED: "No contact linked yet.",
 * slice 5's suggestions for the deal's account (the server drops contacts
 * already linked to this deal before its limit of 2) each with "Link", and
 * "+ Add someone else", which opens New contact pre-filled with the account
 * and links the contact it creates.
 *
 * R6 (0139): each linked row shows its buying-committee role as a chip, and —
 * when the session may set roles (owner/admin from /v1/me, or the server's
 * viewerMayAct for the deal's linked owner; fail closed) — a "Role for …"
 * picker; a coverage line under the rows names the roles held and says when no
 * decision maker is set. Roles are set by a person, never inferred.
 *
 * The SINGLE-THREADED warning: the deal's fenced GBP value (GBP → its value,
 * else its captured base; an uncaptured non-GBP value is not weighed) is at
 * least £1M and exactly one contact is linked. Every write is the server's
 * (owner/admin); a refusal shows the server's own sentence.
 */
import { useState } from 'react'
import { Mail, Phone, Sparkles } from 'lucide-react'
import { toast } from 'sonner'
import type { DealDetail, SalesContact } from '../../types'
import type { SalesDealContacts } from '../../../../api/types'
import {
  useAsync, fetchContact, fetchContactSuggestions, linkDealContact, unlinkDealContact, setPrimaryDealContact, fetchDealCard, setDealContactRole,
} from '../../api'
import { apiErrorMessage } from '../../../../api/client'
import { Avatar } from '../../DashboardShared'
import { useLogActivity } from './logActivityShared'
import { useWriteAccess } from '../../../../lib/writeAccess'
import { NewContactDrawer } from './SalesContacts'
import { info, warning } from '../../../../theme/tokens'
import { singleThreadedWarning, COMMITTEE_ROLES, roleLabel, coverageSentence, rolesSupported } from './dealPeople'
import type { DealContactRole } from '../../../../api/types'

export function DealPeopleCard({ deal, links, loading, failed, onLinksChanged, canSetRoles = false }: {
  deal: DealDetail
  links: SalesDealContacts | null
  loading: boolean
  failed: boolean
  onLinksChanged: (next: SalesDealContacts) => void
  /** R6: the session may set committee roles on this deal. Fail closed. */
  canSetRoles?: boolean
}) {
  const logActivity = useLogActivity()
  // Link / unlink / set primary / add are records-level writes
  // (lib/writeAccess; fail closed). The committee role picker keeps its own
  // per-deal canSetRoles.
  const { records } = useWriteAccess()
  const items = links?.items ?? []
  // R6 roles only where the api serves them (rolesSupported): the picker and
  // the coverage line are hidden on an api without the role route.
  const roles = rolesSupported(deal, items)
  const idsKey = items.map(i => i.contactId).join(',')
  // Each linked contact's card fields (title, email, phone) — best-effort: a
  // failed read degrades that row to its name, never to a raw id.
  const detailsQ = useAsync(
    () => Promise.all(items.map(i => fetchContact(i.contactId).catch(() => null))),
    [idsKey],
  )
  const details: Record<string, SalesContact> = {}
  for (const c of detailsQ.data ?? []) if (c) details[c.id] = c
  const none = links != null && items.length === 0
  const suggestQ = useAsync(
    () => (none && deal.accountId ? fetchContactSuggestions(deal.accountId, deal.id) : Promise.resolve({ items: [] })),
    [deal.id, deal.accountId, none, idsKey],
  )
  const [busy, setBusy] = useState<string | null>(null)
  const [confirmUnlink, setConfirmUnlink] = useState<{ id: string; name: string } | null>(null)
  const [adding, setAdding] = useState(false)

  async function write(key: string, fn: () => Promise<SalesDealContacts>, done: string, fallback: string) {
    if (busy) return
    setBusy(key)
    try {
      onLinksChanged(await fn())
      toast.success(done)
    } catch (e) {
      toast.error(apiErrorMessage(e, fallback))
    } finally {
      setBusy(null)
    }
  }
  async function logCall(contact: SalesContact, back: HTMLElement) {
    if (!logActivity) return
    try {
      const { deal: card } = await fetchDealCard(deal.id)
      logActivity.open({ deal: card, contact, type: 'call' }, () => back)
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to open the call log'))
    }
  }
  const warn = singleThreadedWarning(deal, records)

  return (
    <div className="k-card" data-rail="people">
      <div className="k-card-head"><div className="k-card-title">People on this deal</div></div>
      <div className="p-5 flex flex-col gap-3">
        {loading ? (
          <div className="k-skeleton" style={{ height: 40 }} aria-label="Loading the people on this deal" />
        ) : failed || !links ? (
          <div className="text-13 text-ink-muted">The people on this deal could not be loaded.</div>
        ) : none ? (
          <>
            <div className="text-13 text-ink-muted">No contact linked yet.</div>
            {records && (suggestQ.data?.items.length ?? 0) > 0 && (
              <div className="rounded-[12px] p-3 flex flex-col gap-2" style={{ background: info.bg }} data-people-suggestions>
                <div className="text-12 font-semibold inline-flex items-center gap-1.5" style={{ color: info.text }}>
                  <Sparkles size={12} aria-hidden="true" /> Found at {deal.company || 'this account'}
                </div>
                {suggestQ.data!.items.map(s => (
                  <div key={s.contactId} className="flex items-center gap-3" data-suggestion={s.contactId}>
                    <Avatar name={s.fullName} size={30} />
                    <div className="min-w-0 flex-1">
                      <div className="text-13 font-semibold text-ink-primary truncate">{s.fullName}</div>
                      {s.title && <div className="text-12 text-ink-muted truncate">{s.title}</div>}
                    </div>
                    <button type="button" className="k-btn-secondary k-btn-secondary--flex" disabled={busy !== null}
                      aria-label={`Link ${s.fullName}`}
                      onClick={() => { void write(`link:${s.contactId}`, () => linkDealContact(deal.id, s.contactId), `${s.fullName} linked`, 'Failed to link the contact') }}>
                      Link
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          items.map(it => {
            const c = details[it.contactId]
            return (
              <div key={it.contactId} className="flex flex-col gap-1.5" data-person={it.contactId} data-primary={it.isPrimary ? 'true' : 'false'}>
                <div className="flex items-center gap-3">
                  <Avatar name={it.name} size={34} />
                  <div className="min-w-0 flex-1">
                    <div className="text-14 font-semibold text-ink-primary truncate">{it.name}</div>
                    <div className="text-12 text-ink-muted truncate">{[c?.title, it.isPrimary ? 'Primary contact' : null].filter(Boolean).join(' · ')}</div>
                  </div>
                  {it.role && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-11 font-medium flex-shrink-0"
                      style={{ background: info.bg, color: info.text }} data-role-chip={it.role}>
                      {roleLabel(it.role)}
                    </span>
                  )}
                  {logActivity && c && (
                    <button type="button" className="k-btn-secondary k-btn-secondary--flex inline-flex items-center gap-1"
                      aria-label={`Log call with ${it.name}`} onClick={e => { void logCall(c, e.currentTarget) }}>
                      <Phone size={13} aria-hidden="true" /> Log call
                    </button>
                  )}
                </div>
                <div className="flex flex-col gap-1 text-13 text-ink-secondary pl-[46px]">
                  {c?.email && <a className="inline-flex items-center gap-2 hover:underline" href={`mailto:${c.email}`}><Mail size={13} className="text-ink-faint" aria-hidden="true" />{c.email}</a>}
                  {c?.phone && <a className="inline-flex items-center gap-2 hover:underline" href={`tel:${c.phone}`}><Phone size={13} className="text-ink-faint" aria-hidden="true" />{c.phone}</a>}
                  {c && !c.email && !c.phone && <span className="text-ink-muted">No email or phone on file</span>}
                  <span className="inline-flex items-center gap-3 text-12">
                    {records && !it.isPrimary && (
                      <button type="button" className="font-medium hover:underline" style={{ color: info.text }} disabled={busy !== null}
                        aria-label={`Set ${it.name} as primary`}
                        onClick={() => { void write(`primary:${it.contactId}`, () => setPrimaryDealContact(deal.id, it.contactId), `${it.name} is now the primary contact`, 'Failed to set the primary contact') }}>
                        Set primary
                      </button>
                    )}
                    {records && (
                      <button type="button" className="font-medium hover:underline text-ink-secondary" disabled={busy !== null}
                        aria-label={`Unlink ${it.name}`} onClick={() => setConfirmUnlink({ id: it.contactId, name: it.name })}>
                        Unlink
                      </button>
                    )}
                    {canSetRoles && roles && (
                      <select className="text-12 border border-edge-control rounded-control px-1.5 py-0.5 text-ink-secondary" disabled={busy !== null}
                        aria-label={`Role for ${it.name}`} value={it.role ?? ''}
                        onChange={e => {
                          const next = (e.target.value || null) as DealContactRole | null
                          const label = roleLabel(next)
                          void write(`role:${it.contactId}`, () => setDealContactRole(deal.id, it.contactId, next),
                            label ? `${it.name}: ${label}` : `${it.name}: role cleared`, 'Failed to set the role')
                        }}>
                        <option value="">No role</option>
                        {COMMITTEE_ROLES.map(r => <option key={r.role} value={r.role}>{r.label}</option>)}
                      </select>
                    )}
                  </span>
                </div>
              </div>
            )
          })
        )}
        {items.length > 0 && roles && (
          <div className="text-12 text-ink-muted" data-committee-coverage>
            <span className="font-semibold text-ink-secondary">Buying committee:</span> {coverageSentence(items)}
          </div>
        )}
        {warn && (
          <div className="rounded-[12px] px-3 py-2 text-13" style={{ background: warning.bg, color: warning.text }} data-single-threaded>{warn}</div>
        )}
        {links && records && (
          <button type="button" className="self-start text-13 font-medium hover:underline" style={{ color: info.text }} onClick={() => setAdding(true)}>
            + Add someone else
          </button>
        )}
      </div>

      {confirmUnlink && records && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setConfirmUnlink(null)}>
          <div role="alertdialog" aria-modal="true" aria-labelledby="unlink-title" aria-describedby="unlink-body"
            className="k-card p-5 w-[400px] max-w-[92vw]" onClick={e => e.stopPropagation()}>
            <div id="unlink-title" className="text-16 font-semibold text-ink-primary">Unlink {confirmUnlink.name}?</div>
            <p id="unlink-body" className="text-13 text-ink-secondary mt-2">
              {confirmUnlink.name} is removed from {deal.title}. The contact itself is kept.
            </p>
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" className="k-btn-quiet" onClick={() => setConfirmUnlink(null)}>Cancel</button>
              <button type="button" className="k-btn" disabled={busy !== null}
                onClick={() => {
                  const target = confirmUnlink
                  setConfirmUnlink(null)
                  void write(`unlink:${target.id}`, () => unlinkDealContact(deal.id, target.id), `${target.name} unlinked`, 'Failed to unlink the contact')
                }}>
                Unlink
              </button>
            </div>
          </div>
        </div>
      )}

      {adding && records && (
        <NewContactDrawer
          initialAccount={deal.accountId ? { accountId: deal.accountId, company: deal.company } : undefined}
          onClose={() => setAdding(false)}
          onCreated={created => {
            setAdding(false)
            void write(`link:${created.id}`, () => linkDealContact(deal.id, created.id), `${created.name} linked`, 'Failed to link the contact')
          }}
        />
      )}
    </div>
  )
}
